import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTransaction } from '../contexts/TransactionContext';
import { usePlans } from '../contexts/PlanContext';
import { useAccounts } from '../contexts/AccountContext';
import { useCategories } from '../contexts/CategoryContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { categoryService } from '../services/categoryService';
import TransactionCard from '../components/transaction/TransactionCard';
import TransactionRow from '../components/transaction/TransactionRow';
import { PixelHourglass } from '../components/PixelHourglass';
import type { TransactionFormData, CategorySplitWithData } from '../components/transaction/types';
import { createEmptyTransaction } from '../components/transaction/types';
import type { CategoryWithMonthData } from '../types/category';
import type { Transaction } from '../types/transaction';

interface MultiTransactionPageProps {
  onBack: () => void;
  mode?: 'add' | 'edit';
}

export default function MultiTransactionPage({ onBack, mode = 'add' }: MultiTransactionPageProps) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const editIds = searchParams.get('ids')?.split(',') || [];

  const { createTransaction, updateTransaction, fetchTransactionById } = useTransaction();
  const { currentMonthPlan, allMonthPlans } = usePlans();
  const { accounts, refreshAccounts } = useAccounts();
  const { refreshCategories } = useCategories();
  const { formatMoney } = useUserPreferences();

  // Transactions state
  const [transactions, setTransactions] = useState<TransactionFormData[]>([]);
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Budget month categories cache (keyed by month_plan_id)
  const [categoriesCache, setCategoriesCache] = useState<Record<string, CategoryWithMonthData[]>>({});
  const [loadingCategories, setLoadingCategories] = useState<Record<string, boolean>>({});

  // Ref for scrolling to new card
  const lastCardRef = useRef<HTMLDivElement>(null);

  // Ref to track if edit mode has been initialized (prevents re-loading on tab focus)
  const hasInitializedRef = useRef(false);

  // Format month from YYYY-MM-DD to readable format
  const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const formatMonth = (monthStr: string): string => {
    const date = new Date(monthStr);
    return `${t(`budget.months.${monthKeys[date.getMonth()]}`)} ${date.getFullYear()}`;
  };

  // Fetch categories for a month plan
  const fetchCategoriesForMonth = useCallback(async (monthPlanId: string) => {
    if (categoriesCache[monthPlanId] || loadingCategories[monthPlanId]) return;

    setLoadingCategories(prev => ({ ...prev, [monthPlanId]: true }));
    try {
      const data = await categoryService.getCategoriesByMonthPlan(monthPlanId);
      setCategoriesCache(prev => ({ ...prev, [monthPlanId]: data }));
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(prev => ({ ...prev, [monthPlanId]: false }));
    }
  }, [categoriesCache, loadingCategories]);

  // Initialize transactions
  useEffect(() => {
    const initTransactions = async () => {
      if (mode === 'add') {
        // Add mode: start with one empty transaction
        if (currentMonthPlan && transactions.length === 0) {
          setTransactions([createEmptyTransaction(currentMonthPlan.id)]);
          await fetchCategoriesForMonth(currentMonthPlan.id);
        }
      } else if (mode === 'edit') {
        // Skip re-initialization if already loaded (prevents reset on tab focus)
        if (hasInitializedRef.current && transactions.length > 0) {
          return;
        }
        // Edit mode: load transaction(s) by ID(s) - use Promise.all for parallel loading
        setLoading(true);
        try {
          const idsToLoad = id ? [id] : editIds;

          // Load all transactions in parallel
          const txPromises = idsToLoad.map(txId => fetchTransactionById(txId));
          const txResults = await Promise.all(txPromises);

          const loadedTransactions: TransactionFormData[] = [];
          const monthPlanIds = new Set<string>();

          for (const tx of txResults) {
            if (tx) {
              const formData = convertTransactionToFormData(tx);
              loadedTransactions.push(formData);
              if (formData.selectedBudgetMonthPlan) {
                monthPlanIds.add(formData.selectedBudgetMonthPlan);
              }
            }
          }

          // Fetch categories for all unique month plans in parallel
          const categoryPromises = Array.from(monthPlanIds).map(monthPlanId =>
            fetchCategoriesForMonth(monthPlanId)
          );
          await Promise.all(categoryPromises);

          if (loadedTransactions.length > 0) {
            setTransactions(loadedTransactions);
            hasInitializedRef.current = true;
          } else {
            setError(t('transaction.error_load_failed'));
          }
        } catch (err) {
          console.error('Error loading transactions:', err);
          setError(t('transaction.error_load_failed'));
        } finally {
          setLoading(false);
        }
      }
    };

    initTransactions();
  }, [mode, id, currentMonthPlan, fetchTransactionById, fetchCategoriesForMonth, t]);

  // Helper to convert Transaction to FormData
  const convertTransactionToFormData = (tx: Transaction): TransactionFormData => {
    const isTransfer = !!tx.transfer_pair_id;
    let type: 'expense' | 'income' | 'transfer' = isTransfer ? 'transfer' : tx.transaction_type;

    let fromAcc = '';
    let toAcc = '';
    let selectedAcc = tx.account_id || '';

    if (isTransfer) {
      if (tx.transaction_type === 'expense') {
        fromAcc = tx.account_id || '';
        const toAccount = accounts.find(a => a.name === tx.to_account_name);
        toAcc = toAccount?.id || '';
      } else {
        toAcc = tx.account_id || '';
        const fromAccount = accounts.find(a => a.name === tx.from_account_name);
        fromAcc = fromAccount?.id || '';
      }
      selectedAcc = '';
    }

    const categorySplits: CategorySplitWithData[] = [];
    if (tx.category_id && !tx.have_child) {
      categorySplits.push({
        category_id: tx.category_id,
        amount: tx.amount,
        categoryName: tx.category_name || '',
        parentName: tx.parent_category_name || '',
        remainingAllocation: 0, // Will be updated when categories load
      });
    } else if (tx.have_child && tx.children) {
      for (const child of tx.children) {
        categorySplits.push({
          category_id: child.category_id,
          amount: child.amount,
          categoryName: child.category_name,
          parentName: child.parent_category_name || '',
          remainingAllocation: 0,
        });
      }
    }

    return {
      id: crypto.randomUUID(),
      dbId: tx.id,
      type,
      amount: tx.amount,
      categorySplits,
      selectedAccount: selectedAcc,
      fromAccount: fromAcc,
      toAccount: toAcc,
      description: tx.name || '',
      date: tx.date,
      selectedBudgetMonthPlan: tx.month_plan_id,
      isExpanded: true,
      hasError: false,
    };
  };

  // Scroll to last card when a new one is added
  useEffect(() => {
    if (lastCardRef.current && transactions.length > 1) {
      lastCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [transactions.length]);

  // Handlers
  const handleAddTransaction = () => {
    if (mode !== 'add') return;

    // Duplicate ALL values from last transaction (including amount, desc, category)
    const lastTx = transactions[transactions.length - 1];
    const newTx: TransactionFormData = {
      id: crypto.randomUUID(),
      type: lastTx?.type || 'expense',
      amount: lastTx?.amount || 0,
      categorySplits: lastTx?.categorySplits ? [...lastTx.categorySplits] : [],
      selectedAccount: lastTx?.selectedAccount || '',
      fromAccount: lastTx?.fromAccount || '',
      toAccount: lastTx?.toAccount || '',
      description: lastTx?.description || '',
      date: lastTx?.date || new Date().toISOString().split('T')[0],
      selectedBudgetMonthPlan: lastTx?.selectedBudgetMonthPlan || currentMonthPlan?.id || '',
      isExpanded: true,
      hasError: false,
    };

    // Collapse all existing cards
    setTransactions(prev => [
      ...prev.map(tx => ({ ...tx, isExpanded: false })),
      newTx
    ]);

    // Fetch categories if needed
    if (newTx.selectedBudgetMonthPlan && !categoriesCache[newTx.selectedBudgetMonthPlan]) {
      fetchCategoriesForMonth(newTx.selectedBudgetMonthPlan);
    }
  };

  const handleRemoveTransaction = (index: number) => {
    if (mode !== 'add' || transactions.length <= 1) return;

    setTransactions(prev => {
      const newList = prev.filter((_, i) => i !== index);
      // If we removed the only expanded card, expand the first one
      if (!newList.some(tx => tx.isExpanded) && newList.length > 0) {
        newList[0].isExpanded = true;
      }
      return newList;
    });
  };

  const handleUpdateTransaction = (index: number, updates: Partial<TransactionFormData>) => {
    setTransactions(prev => prev.map((tx, i) =>
      i === index ? { ...tx, ...updates } : tx
    ));
  };

  const handleToggleExpand = (index: number) => {
    setTransactions(prev => prev.map((tx, i) => ({
      ...tx,
      isExpanded: i === index ? !tx.isExpanded : tx.isExpanded
    })));
  };

  const handleBudgetMonthChange = async (index: number, monthPlanId: string) => {
    // Fetch categories for the new month first (if not cached)
    let newMonthCategories = categoriesCache[monthPlanId];
    if (!newMonthCategories) {
      setLoadingCategories(prev => ({ ...prev, [monthPlanId]: true }));
      try {
        const data = await categoryService.getCategoriesByMonthPlan(monthPlanId);
        setCategoriesCache(prev => ({ ...prev, [monthPlanId]: data }));
        newMonthCategories = data;
      } catch (err) {
        console.error('Error fetching categories:', err);
        newMonthCategories = [];
      } finally {
        setLoadingCategories(prev => ({ ...prev, [monthPlanId]: false }));
      }
    }

    // Get all subcategory IDs from the new month
    const newMonthCategoryIds = new Set(
      newMonthCategories.flatMap(group => (group.children || []).map(cat => cat.id))
    );

    // Get current transaction's category splits
    const currentTransaction = transactions[index];
    const currentSplits = currentTransaction?.categorySplits || [];

    // Filter to keep only categories that exist in the new month
    const preservedSplits = currentSplits.filter(split =>
      newMonthCategoryIds.has(split.category_id)
    );

    // Update with preserved categories (or empty if none match)
    handleUpdateTransaction(index, {
      selectedBudgetMonthPlan: monthPlanId,
      categorySplits: preservedSplits
    });
  };

  // Handle creating multiple rows from category selection (desktop only)
  const handleCreateRowsForCategories = (baseTransaction: TransactionFormData, categoryIds: string[]) => {
    // Get categories data from cache
    const budgetCategories = categoriesCache[baseTransaction.selectedBudgetMonthPlan] || [];
    const allSubcategories = budgetCategories.flatMap(group =>
      (group.children || []).map(cat => ({
        ...cat,
        parentName: group.name
      }))
    );

    // Helper to get pay full data for a category
    const getCategoryPayFullData = (cat: CategoryWithMonthData) => {
      if (cat.is_goal) {
        if (cat.goal_monthly_target) {
          const remaining = Math.max(0, cat.goal_monthly_target - (cat.transaction || 0));
          return { amount: remaining, label: t('transaction.remaining') };
        } else {
          const remaining = Math.max(0, (cat.goal_target || 0) - (cat.goal_saved || 0));
          return { amount: remaining, label: t('transaction.remaining') };
        }
      }
      return { amount: Math.max(0, cat.balance || 0), label: t('transaction.remaining') };
    };

    // Find the current transaction's index
    const currentIndex = transactions.findIndex(tx => tx.id === baseTransaction.id);
    if (currentIndex === -1) return;

    // Create new transactions for each category
    const newTransactions: TransactionFormData[] = categoryIds.map((categoryId, idx) => {
      const categoryData = allSubcategories.find(c => c.id === categoryId);
      const payFullData = categoryData ? getCategoryPayFullData(categoryData) : { amount: 0, label: '' };

      const categorySplit: CategorySplitWithData = {
        category_id: categoryId,
        amount: 0,
        categoryName: categoryData?.name || '',
        parentName: (categoryData as any)?.parentName || '',
        remainingAllocation: payFullData.amount,
        isGoal: categoryData?.is_goal,
        goalTargetMonth: categoryData?.goal_target_month,
        displayLabel: payFullData.label
      };

      // First category updates the current row, rest create new rows
      if (idx === 0) {
        return {
          ...baseTransaction,
          categorySplits: [categorySplit],
          hasError: false,
          errorMessage: undefined
        };
      }

      // Create new row with same metadata but different category
      return {
        ...baseTransaction,
        id: crypto.randomUUID(), // New unique ID
        dbId: undefined, // Not in database yet
        categorySplits: [categorySplit],
        amount: 0, // Reset amount for new row
        hasError: false,
        errorMessage: undefined,
        isExpanded: true
      };
    });

    // Update transactions list: replace current row and insert new ones after it
    setTransactions(prev => {
      const result = [...prev];
      // Replace current row with first new transaction (updated category)
      result[currentIndex] = newTransactions[0];
      // Insert remaining new transactions after current position
      result.splice(currentIndex + 1, 0, ...newTransactions.slice(1));
      return result;
    });
  };

  // Validation
  const validateTransaction = (tx: TransactionFormData, interimBalances?: Record<string, number>): { valid: boolean; message?: string } => {
    // Check date is provided
    if (!tx.date) return { valid: false, message: t('transaction.error_select_date') };

    if (tx.type === 'transfer') {
      if (!tx.fromAccount) return { valid: false, message: t('transaction.error_select_source') };
      if (!tx.toAccount) return { valid: false, message: t('transaction.error_select_destination') };
      if (tx.fromAccount === tx.toAccount) return { valid: false, message: t('transaction.error_same_account') };

      const amount = tx.categorySplits.length > 0
        ? tx.categorySplits.reduce((sum, s) => sum + s.amount, 0)
        : tx.amount;
      if (amount <= 0) return { valid: false, message: t('transaction.error_transfer_amount') };

      // Check interim balance if provided (for sequential validation)
      if (interimBalances) {
        const fromBalance = interimBalances[tx.fromAccount] ?? 0;
        const fromAccount = accounts.find(a => a.id === tx.fromAccount);
        if (fromBalance < amount) {
          return { valid: false, message: t('transaction.error_insufficient_balance', { account: fromAccount?.name || '', balance: formatMoney(fromBalance) }) };
        }
      }
    } else {
      if (!tx.selectedAccount) return { valid: false, message: t('transaction.error_select_fund') };
    }

    if (tx.type === 'expense') {
      if (!tx.selectedBudgetMonthPlan) return { valid: false, message: t('transaction.error_select_budget_month') };
      if (tx.categorySplits.length === 0) return { valid: false, message: t('transaction.error_select_category') };

      const invalidCat = tx.categorySplits.find(s => !s.amount || s.amount <= 0);
      if (invalidCat) return { valid: false, message: t('transaction.error_category_amount', { name: invalidCat.categoryName }) };

      const total = tx.categorySplits.reduce((sum, s) => sum + s.amount, 0);
      if (total <= 0) return { valid: false, message: t('transaction.error_total_zero') };
    }

    if (tx.type === 'income') {
      if (!tx.selectedBudgetMonthPlan) return { valid: false, message: t('transaction.error_select_income_month') };
      if (tx.amount <= 0) return { valid: false, message: t('transaction.error_transfer_amount') };
    }

    return { valid: true };
  };

  // Sequential validation - validates transactions considering balance changes from previous ones
  const validateAllTransactionsSequentially = (txList: TransactionFormData[]): TransactionFormData[] => {
    // Initialize interim balances from current account balances
    const interimBalances: Record<string, number> = {};
    for (const account of accounts) {
      interimBalances[account.id] = account.balance;
    }

    const result: TransactionFormData[] = [];

    for (const tx of txList) {
      const { valid, message } = validateTransaction(tx, interimBalances);

      if (!valid) {
        result.push({ ...tx, hasError: true, errorMessage: message, isExpanded: true });
      } else {
        result.push({ ...tx, hasError: false, errorMessage: undefined });

        // Apply this transaction's effect on interim balances
        const amount = tx.categorySplits.length > 0
          ? tx.categorySplits.reduce((sum, s) => sum + s.amount, 0)
          : tx.amount;

        if (tx.type === 'transfer') {
          // Transfer: from account decreases, to account increases
          interimBalances[tx.fromAccount] = (interimBalances[tx.fromAccount] ?? 0) - amount;
          interimBalances[tx.toAccount] = (interimBalances[tx.toAccount] ?? 0) + amount;
        } else if (tx.type === 'expense') {
          // Expense: account decreases
          interimBalances[tx.selectedAccount] = (interimBalances[tx.selectedAccount] ?? 0) - amount;
        } else if (tx.type === 'income') {
          // Income: account increases
          interimBalances[tx.selectedAccount] = (interimBalances[tx.selectedAccount] ?? 0) + amount;
        }
      }
    }

    return result;
  };

  // Handle Enter key to add new row instead of submitting
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (mode === 'add') {
        handleAddTransaction();
      }
    }
  }, [mode, handleAddTransaction]);

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate all transactions sequentially (considering balance changes from previous transactions)
    const updatedTransactions = validateAllTransactionsSequentially(transactions);
    const allValid = !updatedTransactions.some(tx => tx.hasError);

    setTransactions(updatedTransactions);

    if (!allValid) {
      setError(t('transaction.multi.validation_error'));
      return;
    }

    setSubmitting(true);

    // Build all payloads first (preparation phase)
    const payloads: { tx: TransactionFormData; index: number; payload: any; isUpdate: boolean }[] = [];

    for (let i = 0; i < transactions.length; i++) {
      const tx = transactions[i];
      const finalAmount = tx.categorySplits.length > 0
        ? tx.categorySplits.reduce((sum, s) => sum + s.amount, 0)
        : tx.amount;

      if (mode === 'edit' && tx.dbId) {
        // Update existing transaction
        const updatePayload: any = {
          transaction_type: tx.type,
          name: tx.description || undefined,
          amount: finalAmount,
          date: tx.date,
          is_recurring: false,
        };

        if (tx.type === 'transfer') {
          updatePayload.from_account_id = tx.fromAccount;
          updatePayload.to_account_id = tx.toAccount;
          if (tx.categorySplits.length > 0) {
            updatePayload.category_id = tx.categorySplits[0].category_id;
            updatePayload.month_plan_id = tx.selectedBudgetMonthPlan;
          } else {
            updatePayload.category_id = null;
            updatePayload.month_plan_id = null;
          }
        } else {
          updatePayload.account_id = tx.selectedAccount;
          if (tx.type === 'expense' && tx.categorySplits.length > 0) {
            updatePayload.category_id = tx.categorySplits[0].category_id;
            updatePayload.month_plan_id = tx.selectedBudgetMonthPlan;
          }
        }

        payloads.push({ tx, index: i, payload: updatePayload, isUpdate: true });
      } else {
        // Create new transaction
        const createPayload: any = {
          transaction_type: tx.type,
          name: tx.description || undefined,
          amount: finalAmount,
          month_plan_id: tx.selectedBudgetMonthPlan,
          date: tx.date,
          is_recurring: false,
        };

        if (tx.type === 'transfer') {
          createPayload.from_account_id = tx.fromAccount;
          createPayload.to_account_id = tx.toAccount;
          if (tx.categorySplits.length > 0) {
            if (tx.categorySplits.length === 1) {
              createPayload.category_id = tx.categorySplits[0].category_id;
            } else {
              createPayload.category_splits = tx.categorySplits.map(s => ({
                category_id: s.category_id,
                amount: s.amount
              }));
              createPayload.is_split_history = true;
            }
          }
        } else {
          createPayload.account_id = tx.selectedAccount;
          if (tx.type === 'expense' && tx.categorySplits.length > 0) {
            if (tx.categorySplits.length === 1) {
              createPayload.category_id = tx.categorySplits[0].category_id;
            } else {
              createPayload.category_splits = tx.categorySplits.map(s => ({
                category_id: s.category_id,
                amount: s.amount
              }));
              createPayload.is_split_history = true;
            }
          }
        }

        payloads.push({ tx, index: i, payload: createPayload, isUpdate: false });
      }
    }

    // Execute all transactions - stop on first error and mark which one failed
    try {
      for (const { tx, index, payload, isUpdate } of payloads) {
        try {
          if (isUpdate && tx.dbId) {
            await updateTransaction(tx.dbId, payload);
          } else {
            await createTransaction(payload);
          }
        } catch (err) {
          // Mark the failing transaction with error
          const errorMessage = err instanceof Error ? err.message : t('transaction.error_add_failed');
          setTransactions(prev => prev.map((t, i) =>
            i === index
              ? { ...t, hasError: true, errorMessage: `#${index + 1}: ${errorMessage}`, isExpanded: true }
              : t
          ));
          throw new Error(`${t('transaction.multi.error_at_row', { row: index + 1 })}: ${errorMessage}`);
        }
      }

      await refreshAccounts();
      await refreshCategories();

      // Navigate back immediately (optimistic)
      onBack();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('transaction.error_add_failed');
      setError(errorMessage);
      console.error('Error saving transactions:', err);
      setSubmitting(false);
    }
  };

  // Get submit button text
  const getSubmitButtonText = () => {
    if (submitting) return t('transaction.saving');
    if (mode === 'edit') return t('transaction.save_changes');
    if (transactions.length > 1) return t('transaction.multi.save_all');
    return t('transaction.save');
  };

  // Loading state with PixelHourglass
  if (loading) {
    return (
      <div className="min-h-screen bg-[#ffffeb] flex items-center justify-center">
        <div className="text-center">
          <PixelHourglass />
          <p className="text-[#077f5d] font-mono mt-4">{t('transaction.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header */}
      <div
        className="bg-[#3cbe8c] text-white px-6 py-4 border-b-4 border-[#077f5d]"
        style={{ boxShadow: '0 4px 0 #077f5d' }}
      >
        <div className="max-w-4xl lg:max-w-none mx-auto flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-[#2da877] border-2 border-[#077f5d]"
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className="uppercase flex-1"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '14px',
              lineHeight: '1.4'
            }}
          >
            {mode === 'edit' ? t('transaction.edit_title') : t('transaction.add_title')}
          </h1>
          {transactions.length > 1 && (
            <span className="text-sm font-mono bg-[#077f5d] px-2 py-1 rounded">
              {transactions.length} {t('transaction.multi.items')}
            </span>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {submitting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white border-4 border-[#077f5d] p-8 text-center" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
            <PixelHourglass />
            <p className="text-[#077f5d] font-mono mt-4 text-sm">{t('transaction.saving')}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="px-4 lg:px-6 py-6 max-w-4xl lg:max-w-none mx-auto">
        {/* Error Message */}
        {error && (
          <div
            className="bg-red-50 border-2 border-red-600 text-red-700 px-4 py-3 mb-4 font-mono"
            style={{ boxShadow: '3px 3px 0 #dc2626' }}
          >
            {error}
          </div>
        )}

        {/* Mobile: Transaction Cards */}
        <div className="lg:hidden space-y-4">
          {transactions.map((tx, index) => (
            <div
              key={tx.id}
              ref={index === transactions.length - 1 ? lastCardRef : null}
            >
              <TransactionCard
                transaction={tx}
                index={index}
                mode={mode}
                isOnlyCard={transactions.length === 1}
                accounts={accounts}
                allMonthPlans={allMonthPlans}
                budgetMonthCategories={categoriesCache[tx.selectedBudgetMonthPlan] || []}
                loadingBudgetCategories={loadingCategories[tx.selectedBudgetMonthPlan] || false}
                formatMoney={formatMoney}
                formatMonth={formatMonth}
                onUpdate={handleUpdateTransaction}
                onRemove={handleRemoveTransaction}
                onToggleExpand={handleToggleExpand}
                onBudgetMonthChange={handleBudgetMonthChange}
              />
            </div>
          ))}
        </div>

        {/* Desktop: Table with Transaction Rows */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full border-2 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
            <thead>
              <tr className="bg-[#3cbe8c] text-white border-b-2 border-[#077f5d]">
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '40px', minWidth: '40px' }}>#</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '110px', minWidth: '110px' }}>{t('transaction.type_label')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '150px', minWidth: '150px' }}>{t('transaction.source_fund')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '150px', minWidth: '150px' }}>{t('transaction.to_account')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '120px', minWidth: '120px' }}>{t('transaction.budget_month_short')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '180px', minWidth: '180px' }}>{t('transaction.category')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '110px', minWidth: '110px' }}>{t('transaction.amount')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '150px', minWidth: '150px' }}>{t('transaction.description')}</th>
                <th className="px-1 py-3 text-left font-mono text-[10px] uppercase" style={{ width: '120px', minWidth: '120px' }}>{t('transaction.date')}</th>
                <th className="px-1 py-3 text-center font-mono text-[10px] uppercase" style={{ width: '44px', minWidth: '44px' }}></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, index) => (
                <TransactionRow
                  key={tx.id}
                  transaction={tx}
                  index={index}
                  mode={mode}
                  isOnlyCard={transactions.length === 1}
                  accounts={accounts}
                  allMonthPlans={allMonthPlans}
                  budgetMonthCategories={categoriesCache[tx.selectedBudgetMonthPlan] || []}
                  loadingBudgetCategories={loadingCategories[tx.selectedBudgetMonthPlan] || false}
                  formatMoney={formatMoney}
                  formatMonth={formatMonth}
                  onUpdate={handleUpdateTransaction}
                  onRemove={handleRemoveTransaction}
                  onToggleExpand={handleToggleExpand}
                  onBudgetMonthChange={handleBudgetMonthChange}
                  onCreateRowsForCategories={handleCreateRowsForCategories}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Transaction Button - only in add mode */}
        {mode === 'add' && (
          <button
            type="button"
            onClick={handleAddTransaction}
            className="w-full mt-4 py-3 border-2 border-dashed border-[#077f5d] text-[#077f5d] font-bold hover:bg-[#dffbd0] flex items-center justify-center gap-2 font-mono"
            style={{ transition: 'none' }}
          >
            <Plus className="w-5 h-5" />
            {t('transaction.multi.add_another')}
          </button>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full mt-6 bg-[#3cbe8c] text-white py-4 border-2 border-[#077f5d] font-bold text-lg hover:bg-[#2da877] disabled:opacity-50 disabled:cursor-not-allowed uppercase"
          style={{
            boxShadow: '6px 6px 0 #077f5d',
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '12px'
          }}
          onMouseEnter={(e) => {
            if (!submitting) {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(2px, 2px)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(0, 0)';
          }}
        >
          {getSubmitButtonText()}
        </button>
      </form>
    </div>
  );
}
